import { useEffect, useRef } from "react";

interface Pipeline {
  id: string;
  points: [number, number][];
  pressure: number;
  flow: number;
  diameter: number;
  material: string;
  status: "normal" | "warning" | "alarm";
}

const pipelines: Pipeline[] = [
  { id: "PL001", points: [[200, 150], [400, 150], [600, 150]], pressure: 0.35, flow: 1250, diameter: 200, material: "PE管", status: "normal" },
  { id: "PL002", points: [[400, 150], [400, 300], [400, 450]], pressure: 0.28, flow: 860, diameter: 150, material: "钢管", status: "normal" },
  { id: "PL003", points: [[600, 150], [750, 200], [850, 300]], pressure: 0.41, flow: 320, diameter: 100, material: "PE管", status: "alarm" },
  { id: "PL004", points: [[200, 150], [200, 300], [200, 450]], pressure: 0.32, flow: 680, diameter: 150, material: "钢管", status: "normal" },
  { id: "PL005", points: [[200, 450], [400, 450], [600, 450]], pressure: 0.25, flow: 420, diameter: 100, material: "PE管", status: "warning" },
  { id: "PL006", points: [[600, 150], [600, 300], [600, 450]], pressure: 0.30, flow: 540, diameter: 125, material: "钢管", status: "normal" },
  { id: "PL007", points: [[400, 300], [550, 300], [700, 300]], pressure: 0.27, flow: 380, diameter: 100, material: "PE管", status: "normal" },
];

const stations = [
  { id: "S1", x: 200, y: 150, name: "朝阳调压站", pressure: 0.35, flow: 1250 },
  { id: "S2", x: 600, y: 150, name: "东城调压站", pressure: 0.41, flow: 320 },
  { id: "S3", x: 200, y: 450, name: "丰台调压站", pressure: 0.25, flow: 420 },
  { id: "S4", x: 600, y: 450, name: "海淀调压站", pressure: 0.30, flow: 540 },
  { id: "S5", x: 400, y: 300, name: "西城调压站", pressure: 0.28, flow: 860 },
];

const valves = [
  { id: "V1", x: 300, y: 150, open: true },
  { id: "V2", x: 500, y: 150, open: true },
  { id: "V3", x: 400, y: 250, open: true },
  { id: "V4", x: 400, y: 380, open: false },
  { id: "V5", x: 300, y: 450, open: true },
  { id: "V6", x: 500, y: 450, open: true },
  { id: "V7", x: 600, y: 300, open: true },
];

const pressurePoints = [
  { id: "P1", x: 350, y: 150, value: 0.33, status: "normal" },
  { id: "P2", x: 680, y: 200, value: 0.41, status: "alarm" },
  { id: "P3", x: 200, y: 350, value: 0.29, status: "normal" },
  { id: "P4", x: 500, y: 300, value: 0.27, status: "normal" },
  { id: "P5", x: 500, y: 450, value: 0.24, status: "warning" },
];

export default function PipelineFlow() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const offsetRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;

    const getColor = (status: string) => {
      if (status === "alarm") return "#ef4444";
      if (status === "warning") return "#f59e0b";
      return "#22c55e";
    };

    const drawArrow = (ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, color: string) => {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
      const arrowSize = 8;
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;
      ctx.save();
      ctx.translate(mx, my);
      ctx.rotate(angle);
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.8;
      ctx.beginPath();
      ctx.moveTo(arrowSize, 0);
      ctx.lineTo(-arrowSize, -arrowSize * 0.6);
      ctx.lineTo(-arrowSize, arrowSize * 0.6);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      void len;
    };

    const animate = () => {
      ctx.clearRect(0, 0, W, H);

      // Background grid
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 40) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = 0; y < H; y += 40) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      // Draw pipelines
      pipelines.forEach(pl => {
        const color = getColor(pl.status);
        const width = pl.diameter / 40;

        // Pipeline base
        ctx.beginPath();
        ctx.strokeStyle = color + "40";
        ctx.lineWidth = width + 4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (let i = 0; i < pl.points.length - 1; i++) {
          if (i === 0) ctx.moveTo(pl.points[i][0], pl.points[i][1]);
          ctx.lineTo(pl.points[i + 1][0], pl.points[i + 1][1]);
        }
        ctx.stroke();

        // Pipeline main
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        for (let i = 0; i < pl.points.length - 1; i++) {
          if (i === 0) ctx.moveTo(pl.points[i][0], pl.points[i][1]);
          ctx.lineTo(pl.points[i + 1][0], pl.points[i + 1][1]);
        }
        ctx.stroke();

        // Flow animation dots
        for (let i = 0; i < pl.points.length - 1; i++) {
          const p1 = pl.points[i];
          const p2 = pl.points[i + 1];
          const segLen = Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2);
          const dotCount = Math.floor(segLen / 30);
          const speed = pl.flow / 500;

          for (let d = 0; d < dotCount; d++) {
            const t = ((d / dotCount) + (offsetRef.current * speed * 0.01)) % 1;
            const x = p1[0] + (p2[0] - p1[0]) * t;
            const y = p1[1] + (p2[1] - p1[1]) * t;

            ctx.beginPath();
            ctx.arc(x, y, 3, 0, Math.PI * 2);
            ctx.fillStyle = "rgba(255,255,255,0.9)";
            ctx.fill();
          }

          // Flow direction arrow
          drawArrow(ctx, p1[0], p1[1], p2[0], p2[1], color);
        }

        // Flow label at midpoint
        const midIdx = Math.floor(pl.points.length / 2);
        const mx = (pl.points[midIdx - 1][0] + pl.points[midIdx][0]) / 2;
        const my = (pl.points[midIdx - 1][1] + pl.points[midIdx][1]) / 2;
        ctx.fillStyle = "rgba(0,0,0,0.7)";
        ctx.fillRect(mx - 28, my - 12, 56, 16);
        ctx.fillStyle = color;
        ctx.font = "bold 10px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(`${pl.flow}m³/h`, mx, my);
      });

      // Draw stations
      stations.forEach(s => {
        // Glow
        const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, 20);
        grad.addColorStop(0, "rgba(232,83,26,0.4)");
        grad.addColorStop(1, "rgba(232,83,26,0)");
        ctx.beginPath();
        ctx.arc(s.x, s.y, 20, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Station circle
        ctx.beginPath();
        ctx.arc(s.x, s.y, 12, 0, Math.PI * 2);
        ctx.fillStyle = "#E8531A";
        ctx.fill();
        ctx.strokeStyle = "white";
        ctx.lineWidth = 2;
        ctx.stroke();

        // Station icon
        ctx.fillStyle = "white";
        ctx.font = "bold 10px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("站", s.x, s.y + 4);

        // Label
        ctx.fillStyle = "rgba(0,0,0,0.8)";
        ctx.fillRect(s.x - 40, s.y + 16, 80, 28);
        ctx.fillStyle = "white";
        ctx.font = "10px sans-serif";
        ctx.fillText(s.name, s.x, s.y + 27);
        ctx.fillStyle = "#22c55e";
        ctx.font = "bold 9px sans-serif";
        ctx.fillText(`${s.pressure}MPa · ${s.flow}m³/h`, s.x, s.y + 38);
      });

      // Draw valves
      valves.forEach(v => {
        ctx.save();
        ctx.translate(v.x, v.y);
        ctx.rotate(Math.PI / 4);
        ctx.fillStyle = v.open ? "#22c55e" : "#ef4444";
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1.5;
        ctx.fillRect(-6, -6, 12, 12);
        ctx.strokeRect(-6, -6, 12, 12);
        ctx.restore();
      });

      // Draw pressure points
      pressurePoints.forEach(p => {
        const color = p.status === "alarm" ? "#ef4444" : p.status === "warning" ? "#f59e0b" : "#06b6d4";
        const pulse = Math.sin(Date.now() / 500) * 0.3 + 0.7;

        if (p.status !== "normal") {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 14 * pulse, 0, Math.PI * 2);
          ctx.fillStyle = color + "30";
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, 10, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = "white";
        ctx.font = "bold 8px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(p.value.toFixed(2), p.x, p.y + 3);
      });

      offsetRef.current += 1;
      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <div className="w-full h-full bg-[#0a1628] rounded-xl overflow-hidden relative">
      <canvas ref={canvasRef} width={900} height={560} className="w-full h-full" style={{ imageRendering: "crisp-edges" }} />

      {/* Legend */}
      <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-xl p-3 space-y-1.5">
        <p className="text-white/60 text-[10px] font-semibold mb-2">管网图例</p>
        {[
          { color: "#22c55e", label: "正常管线" },
          { color: "#f59e0b", label: "预警管线" },
          { color: "#ef4444", label: "告警管线" },
          { color: "#E8531A", label: "调压站" },
          { color: "#22c55e", label: "开启阀门" },
          { color: "#ef4444", label: "关闭阀门" },
          { color: "#06b6d4", label: "压力监测" },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }}></div>
            <span className="text-white/70 text-[10px]">{item.label}</span>
          </div>
        ))}
      </div>

      {/* Real-time stats */}
      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-xl p-3 space-y-2">
        <p className="text-white/60 text-[10px] font-semibold mb-2">实时流量</p>
        {pipelines.slice(0, 4).map(pl => (
          <div key={pl.id} className="flex items-center justify-between gap-4">
            <span className="text-white/60 text-[10px]">{pl.id}</span>
            <div className="flex items-center gap-1">
              <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${(pl.flow / 1500) * 100}%`,
                    backgroundColor: pl.status === "alarm" ? "#ef4444" : pl.status === "warning" ? "#f59e0b" : "#22c55e"
                  }}
                />
              </div>
              <span className="text-white/80 text-[10px] font-mono w-16 text-right">{pl.flow}m³/h</span>
            </div>
          </div>
        ))}
      </div>

      {/* Flow animation indicator */}
      <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
        <span className="text-white/70 text-[10px]">管线流向动画实时运行中</span>
      </div>
    </div>
  );
}
